<template>
  <v-row no-gutters>
    <v-col :cols="12" class="theme elevation-2 py-1">
      <v-row no-gutters class="text-capitalize" justify="center" align="center">
        <slot name="main-settings"/>
        <v-col :cols="4">
          <v-btn :small="$vuetify.breakpoint.smOnly"
                 :x-small="$vuetify.breakpoint.xsOnly"
                 color="primary"
                 @click="instrumentDialog = true">
            <span class="font-weight-black caption text-capitalize">
              {{ getPlayerInstrument(player) }}
            </span>
          </v-btn>
        </v-col>
        <v-col>
          <v-btn icon color="error" :disabled="player=='lead'" @click="deleteVoice">
            <app-icon icon="delete"/>
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
    <v-col :cols="12" class="pt-1">
      <slot name="harmony"/>
    </v-col>
    <v-col :cols="12" class="no-select elevation-2 py-0 my-0">
      <v-row justify="space-around" class="accent mx-0">
        <v-radio-group row dense hide-details
                       class="accent mt-0"
                       v-model="chordMenu">
          <v-radio v-for="(opt,i) in inputOptions"
                   :key="opt.tabName"
                   color="primary"
                   :label="opt.tabName"
                   :value="i"/>
        </v-radio-group>
      </v-row>
      <select-chord-in-context v-if="chordMenu!==2"
                               :data="getScale.chords(chordMenu)"
                               :player="player"
                               @addData="addData"/>
      <select-any-chord v-else @addData="addData"/>
    </v-col>
    <v-col :cols="12" class="elevation-2">
      <v-row no-gutters align="center" justify="center">
        <v-col :cols="1">
          <v-btn icon x-small
                 :disabled="score.getVoice(player).length===0"
                 @click="toggleMini">
            <app-icon :icon="isMini ? 'call-made' : 'call-received'" :size="18"/>
          </v-btn>
        </v-col>
        <v-col :cols="1">
          <v-btn icon x-small
                 :color="getIsPlayerMuted(player) ? 'error' : 'success'"
                 @click="setIsPlayerMuted({playerName: player})">
            <app-icon :icon="getIsPlayerMuted(player) ? 'volume-mute' : 'volume-up'"
                      :size="18"/>
          </v-btn>
        </v-col>
        <v-col :cols="1">
          <v-btn icon x-small
                 :color="getIsPlayerSolo(player) ? 'success' : 'grey'"
                 @click="setIsPlayerSolo({playerName: player})">
            <span class="body-2">
              S
            </span>
          </v-btn>
        </v-col>
        <v-col>
          <app-slider value-name="volume" :initial-value="getPlayerVolume(player)" :min="-60" :max="12"
                      @valueChanged="setVolume"/>
        </v-col>
      </v-row>
    </v-col>
    <v-col :cols="12" v-if="score.getVoice(player).length">
      <v-row no-gutters class="score">
        <measure v-for="(measureData,i) in score.getVoice(player)"
                 :key="i"
                 :mini="miniMeasures[i]"
                 :player="player"
                 :measure="measureData"
                 :measure-index="i"
                 :selected-measure="selectedMeasure"
                 @playMeasure="playMeasure"
                 @transposeMeasure="transposeMeasure"
                 @editMeasure="editMeasure"
                 @cloneMeasure="cloneMeasure"
                 @deleteMeasure="deleteMeasure"
                 @toggleMeasure="toggleMeasure"/>
      </v-row>
    </v-col>
    <v-dialog fullscreen v-model="instrumentDialog">
      <select-instrument :player="player" @close="instrumentDialog=false"/>
    </v-dialog>
  </v-row>
</template>

<script>
  import { mapActions, mapGetters }                                             from 'vuex'
  import { loadAudioFiles }                                                     from 'utilities/Utilities'
  import { addHarmonyToScore, createPitchClass, getModelHelper, transposeNote } from 'helpers/note-art-helpers'

  export default {
    name: 'ScoreRole',

    components: {
      SelectChordInContext: () => import(/* webpackChunkName: "feature-sections" */ 'sections/app/SelectChordInContext'),
      SelectAnyChord:       () => import(/* webpackChunkName: "feature-sections" */ 'sections/app/SelectAnyChord'),
      Measure:              () => import(/* webpackChunkName: "feature-sections" */ 'sections/app/Measure'),
      AppSlider:            () => import(/* webpackChunkName: "components" */ 'components/AppSlider'),
      SelectInstrument:     () => import(/* webpackChunkName: "select-instrument" */ 'sections/app/SelectInstrument'),
    },

    created() {
      const savedMeasureState = this.getRoleMiniMeasures(this.player)
      if(savedMeasureState) {
        this.miniMeasures = this.getRoleMiniMeasures(this.player)
      } else {
        const length      = this.score.getVoice(this.player).length
        this.miniMeasures = new Array(length).fill(true)
      }
    },

    beforeDestroy() {
      this.setPlayerMiniMeasures({ role: this.player, data: this.miniMeasures })
    },

    props: {
      player: { type: String },
      score:  { type: Object },
    },

    data() {
      return {
        selectedMeasure:  null,
        inputOptions:     [
          { tabName: 'Triads' },
          { tabName: 'Seventh' },
          { tabName: 'Any' },
        ],
        chord_types:      ['root', '1st Inversion', '2nd Inversion'],
        pieceView:        null,
        measure:          0,
        chordMenu:        0,
        miniMeasures:     [],
        isMini:           false,
        instrumentDialog: false,
      }
    },

    computed: {
      ...mapGetters('context', ['getPitch', 'getOctave', 'getScale', 'getHarmony', 'getLineName']),
      ...mapGetters('studio', ['getRoleMiniMeasures']),
      ...mapGetters('transport', [
        'getPlayerInstrument', 'getIsPlayerMuted',
        'getIsPlayerSolo', 'getIsTransportStarted', 'getPlayerVolume',
      ]),
    },

    methods: {
      ...mapActions('view', ['setSnackbar']),
      ...mapActions('studio', ['setPlayerMiniMeasures']),
      ...mapActions('transport', [
        'setIsPlayerMuted',
        'setPlayerVolume',
        'setIsPlayerSolo',
      ]),

      addData(chord) {
        let measure
        if(this.selectedMeasure === null) {
          this.addMeasureToScore()
          measure = this.measure
          this.measure++
          this.miniMeasures.push(this.isMini)
        } else {
          measure = this.selectedMeasure
          this.score.clearMeasure(this.player, measure)
          this.selectedMeasure = null
        }
        const pitchClass = createPitchClass(this.getPitch)
        const octave     = chord.pitchClasses[0].classIndex < pitchClass.classIndex
                           ? this.getOctave + 1
                           : this.getOctave
        const data       = {
          notes:   getModelHelper().pitchClassesToPianoChordNotes(chord.pitchClasses, octave),
          pattern: chord.pattern,
        }
        addHarmonyToScore(this.getLineName, this.score, this.getHarmony.pattern, measure, data, this.player)
        this.updateMeasures()
      },

      addMeasureToScore() {
        while(this.measure < this.score.getVoice(this.player).length) {
          ++this.measure
        }
        this.score.addMeasure(this.player, this.measure)
      },

      deleteMeasure(i) {
        this.score.deleteMeasure(this.player, i)
        this.miniMeasures.splice(i, 1)
        this.measure = this.score.getVoice(this.player).length - 1

        this.setSnackbar({ text: 'Measure deleted!', timeout: 2000, active: true })
        this.updateMeasures()
      },

      playMeasure(measureIndex) {
        this.$emit('playMeasure', measureIndex)
      },

      toggleMeasure(i) {
        this.miniMeasures.splice(i, 1, !this.miniMeasures[i])
      },

      editMeasure(measureIndex) {
        if(this.selectedMeasure !== measureIndex) {
          this.selectedMeasure = measureIndex
        } else {
          this.selectedMeasure = null
        }
      },

      transposeMeasure(measureIndex, interval) {
        const instrument = this.getPlayerInstrument(this.player)
        const note       = transposeNote(`${this.getPitch}${this.getOctave}`, interval)
        loadAudioFiles(instrument, note)
        this.score.transposeMeasure(this.player, measureIndex, interval)
        this.updateMeasures()
      },

      cloneMeasure(measureIndex) {
        this.score.cloneMeasure(this.player, measureIndex)
        this.miniMeasures.splice(measureIndex, 0, this.miniMeasures[measureIndex])
        this.updateMeasures()
      },

      updateMeasures() {
        this.isMini = !this.isMini
        this.isMini = !this.isMini
        if(this.getIsTransportStarted) {
          this.$emit('voiceChanged', false)
        }
      },

      deleteVoice() {
        this.$emit('voiceDeleted', this.player)
      },

      setVolume(value) {
        this.setPlayerVolume({ playerName: this.player, value })
      },

      toggleMini() {
        this.isMini       = !this.isMini
        this.miniMeasures = new Array(this.miniMeasures.length).fill(this.isMini)
      },
    },

    updated() {
      this.measure = this.score.getVoice(this.player).length
    },
  }
</script>

<style scoped>
  .score {
    display: flex;
  }
</style>
